import React from 'react';
import client from '../../api/client';
import usePolling from '../../hooks/usePolling';
import AlarmBanner from '../common/AlarmBanner';
import MetricCard from '../common/MetricCard';
import MetricGrid from '../common/MetricGrid';
import StatusBadge from '../common/StatusBadge';
import GaugeChart from '../charts/GaugeChart';
import BarChart from '../charts/BarChart';
import LoadingSpinner from '../common/LoadingSpinner';
import colors from '../../theme/colors';

const ACTIVE_PACKS = ['battery2', 'battery3', 'battery4', 'battery6', 'battery7', 'battery8'];

function notifyUpdate() {
  window.dispatchEvent(new Event('sv360:data-update'));
}

export default function OverviewPage() {
  const { data, loading } = usePolling(async () => {
    const res = await client.get('/api/overview');
    notifyUpdate();
    return res.data;
  }, 5000);

  if (loading && !data) return <LoadingSpinner />;
  if (!data) return <div style={{ color: colors.textMuted }}>No data</div>;

  const { inverter, batteries, chiller, dehumidifiers } = data;

  // Extract key inverter values from sections
  const status = inverter?.status || {};
  const battery = inverter?.battery || {};
  const grid = inverter?.grid || {};
  const load = inverter?.load || {};
  const energy = inverter?.energy || {};

  // Collect alarms
  const alarms = [];
  const chillerAlarms = chiller?.alarms || {};
  for (const [k, v] of Object.entries(chillerAlarms)) {
    if (v === 1 && !k.startsWith('_') && k !== 'section' && k !== 'device' && k !== 'chiller') {
      alarms.push(`Chiller: ${k}`);
    }
  }
  for (const [unit, sections] of Object.entries(dehumidifiers || {})) {
    const da = sections?.alarms || {};
    for (const [k, v] of Object.entries(da)) {
      if (v === 1 && !k.startsWith('_') && k !== 'section' && k !== 'device' && k !== 'dehumidifier') {
        alarms.push(`${unit}: ${k}`);
      }
    }
  }

  // Energy breakdown for bar chart
  const energyData = [
    { name: 'PV', value: energy.pv_today || 0, color: '#ffd43b' },
    { name: 'Grid Import', value: energy.grid_import_today || 0, color: '#748ffc' },
    { name: 'Grid Export', value: energy.grid_export_today || 0, color: '#cc5de8' },
    { name: 'Bat Charge', value: energy.battery_charge_today || 0, color: '#51cf66' },
    { name: 'Bat Discharge', value: energy.battery_discharge_today || 0, color: '#ff922b' },
    { name: 'Load', value: energy.load_today || 0, color: '#00d4ff' },
  ];

  return (
    <div>
      <AlarmBanner alarms={alarms} />

      {/* System Status Row */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">System Status</div>
        <MetricGrid minWidth={140}>
          <MetricCard label="Inverter State" value={status.inverter_state_name || '--'} decimals={0} />
          <MetricCard label="System State" value={status.system_state_name || '--'} decimals={0} />
          <MetricCard label="Grid Freq" value={grid.frequency} unit="Hz" />
          <MetricCard label="Grid Power" value={grid.power} unit="W" decimals={0} />
          <MetricCard label="Load Power" value={load.power} unit="W" decimals={0} />
          <MetricCard label="Battery Power" value={battery.power} unit="W" decimals={0} />
          <MetricCard label="PV Power" value={status.pv_power || energy.pv_power} unit="W" decimals={0} />
        </MetricGrid>
      </div>

      {/* Battery SoC Gauges */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Battery State of Charge</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
          {ACTIVE_PACKS.map((pack) => {
            const b = batteries?.[pack];
            return (
              <div key={pack} style={{ textAlign: 'center' }}>
                <GaugeChart value={b?.SoC} label={pack} height={140} />
                <div style={{ fontSize: 11, color: colors.textMuted, marginTop: -4 }}>
                  {pack.replace('battery', 'Bat')}
                </div>
                <div style={{ fontSize: 11, color: colors.textDim }}>
                  {b?.Voltage != null ? `${b.Voltage.toFixed(1)}V` : '--'} | {b?.CellTempMax != null ? `${b.CellTempMax.toFixed(0)}°C` : '--'}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom row: Energy + Chiller/Dehum status */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-title">Energy Today</div>
          <BarChart data={energyData} unit="kWh" height={240} />
        </div>
        <div className="card">
          <div className="card-title">Chiller & Dehumidifier Status</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <StatusRow
              label="Chiller 1"
              status={chiller?.control?.system_onoff === 1 ? 'ON' : 'OFF'}
              statusColor={chiller?.control?.system_onoff === 1 ? 'green' : 'gray'}
              detail={`Outlet: ${fmt(chiller?.sensors?.outlet_coolant_temp_c)}°C`}
            />
            {Object.entries(dehumidifiers || {}).map(([unit, sections]) => (
              <StatusRow
                key={unit}
                label={unit.replace('dehumidifier', 'Dehum ')}
                status={sections?.operation?.equipment_status_name || 'Off'}
                statusColor={sections?.operation?.equipment_status === 2 ? 'green' : sections?.operation?.equipment_status === 3 ? 'red' : sections?.operation?.equipment_status === 1 ? 'yellow' : 'gray'}
                detail={`Humidity: ${fmt(sections?.sensors?.indoor_humidity_pct)}%`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusRow({ label, status, statusColor, detail }) {
  return (
    <div style={{
      background: colors.surface,
      borderRadius: 8,
      padding: '10px 14px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <span style={{ fontSize: 13, color: colors.text }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, color: colors.textMuted }}>{detail}</span>
        <StatusBadge label={status} color={statusColor} />
      </div>
    </div>
  );
}

function fmt(v) {
  if (v == null) return '--';
  return typeof v === 'number' ? v.toFixed(1) : v;
}
